import {useState, useEffect, useCallback} from 'react';

function useLocalStorage<T>(
    key: string,
    initialValue: T
): [T, (value: T | ((prevValue: T) => T)) => void] {
    // Get from local storage then parse stored json or return initialValue
    const readValue = useCallback((): T => {
        // Prevent build error "window is undefined" but keep working
        if (typeof window === 'undefined') {
            return initialValue;
        }

        try {
            const item = window.localStorage.getItem(key);
            return item ? (JSON.parse(item) as T) : initialValue;
        } catch (error) {
            console.warn(`Error reading localStorage key "${key}":`, error);
            return initialValue;
        }
        // NOTE: We've removed `initialValue` from the dependency array.
        // This is because we only want to re-run this function when the `key` changes.
        // The `initialValue` is only a fallback for the very first read.
        // Callers commonly pass an object or array literal, so its identity changes every
        // render; depending on it would make readValue unstable for no benefit.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    const [storedValue, setStoredValue] = useState<T>(readValue);

    // Return a wrapped version of useState's setter function that
    // persists the new value to localStorage.
    const setValue = useCallback(
        (value: T | ((prevValue: T) => T)) => {
            try {
                // By using the functional update form of `setStoredValue`,
                // we can get the previous value without needing it in the dependency array.
                setStoredValue(prevStoredValue => {
                    // Allow value to be a function so we have the same API as useState
                    const newValue =
                        value instanceof Function
                            ? value(prevStoredValue)
                            : value;

                    // Save to local storage
                    if (typeof window !== 'undefined') {
                        window.localStorage.setItem(
                            key,
                            JSON.stringify(newValue)
                        );
                    }

                    // Return the new value to update the state
                    return newValue;
                });
            } catch (error) {
                console.warn(`Error setting localStorage key "${key}":`, error);
            }
        },
        [key] // Now `setValue` is stable and only changes if `key` changes.
    );

    // This effect now correctly runs only when the `key` changes,
    // preventing the infinite loop.
    useEffect(() => {
        setStoredValue(readValue());
    }, [readValue]);

    // Subscribe to changes in other tabs/windows
    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === key && event.newValue) {
                try {
                    setStoredValue(JSON.parse(event.newValue));
                } catch (error) {
                    console.warn(
                        `Error parsing localStorage value for key "${key}":`,
                        error
                    );
                }
            }
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('storage', handleStorageChange);
            return () => {
                window.removeEventListener('storage', handleStorageChange);
            };
        }
    }, [key]);

    return [storedValue, setValue];
}

export default useLocalStorage;
